import { useEffect, useRef, useState } from "react";
import demoVideo from "../assets/home/ai-coach-3d-demo-640.mp4";
import demoPoster from "../assets/home/ai-coach-3d-poster-640.webp";

// Reuse SportArcCN media; also recover after page/background restoration.
export function HomeCoachDemo() {
  const [showPoster, setShowPoster] = useState(true);
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let visible = false;
    let userPaused = false;
    let policyPause = false;
    let pageHidden = false;
    function pauseForPolicy() {
      if (!video!.paused) { policyPause = true; video!.pause(); }
    }
    function syncPlayback() {
      if (!visible || document.hidden || pageHidden) pauseForPolicy();
      else if (!reducedMotion.matches && !userPaused && video!.paused) void video!.play().catch(() => {});
    }
    function onPause() {
      if (policyPause) policyPause = false;
      else if (visible && !document.hidden && !pageHidden) userPaused = true;
    }
    function onPlay() {
      userPaused = false;
      if (!visible || document.hidden || pageHidden) pauseForPolicy();
    }
    function onFrame() {
      if (visible && !document.hidden && !pageHidden && video!.readyState >= 2) setShowPoster(false);
    }
    function onPageHide() {
      pageHidden = true;
      if (!userPaused) setShowPoster(true);
      pauseForPolicy();
    }
    function onPageShow() {
      pageHidden = false;
      syncPlayback();
    }
    function onVisibilityChange() {
      if (document.hidden) onPageHide();
      else onPageShow();
    }
    function onMediaReset() { setShowPoster(true); }
    function onMotionChange() {
      video!.autoplay = !reducedMotion.matches;
      if (reducedMotion.matches) pauseForPolicy();
      else syncPlayback();
    }
    video.muted = true;
    video.addEventListener("pause", onPause);
    video.addEventListener("play", onPlay);
    video.addEventListener("timeupdate", onFrame);
    video.addEventListener("emptied", onMediaReset);
    video.addEventListener("error", onMediaReset);
    const observer = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
      syncPlayback();
    }, { threshold: [0, 0.15] });
    if (observer) observer.observe(video);
    else visible = true;
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onPageShow);
    reducedMotion.addEventListener("change", onMotionChange);
    onMotionChange();
    return () => {
      observer?.disconnect();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onPageShow);
      reducedMotion.removeEventListener("change", onMotionChange);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("timeupdate", onFrame);
      video.removeEventListener("emptied", onMediaReset);
      video.removeEventListener("error", onMediaReset);
      video.pause();
    };
  }, []);
  return <figure className="teacher-home-product-visual teacher-home-demo">
    <div className="teacher-home-demo-media">
    <video ref={ref} muted loop playsInline controls preload="metadata" poster={demoPoster} width={640} height={640}
      aria-label="乒乓球 AI教练 3D 动作演示" aria-describedby="teacher-home-demo-caption">
      <source src={demoVideo} type="video/mp4" />
      您的浏览器暂不支持视频播放。
    </video>
    {showPoster && <>
      <img className="teacher-home-demo-poster" src={demoPoster} alt="" aria-hidden="true" width={640} height={640} />
      <button className="teacher-home-demo-play" type="button" onClick={() => { void ref.current?.play().catch(() => {}); }}>播放演示视频</button>
    </>}
    </div>
    <figcaption id="teacher-home-demo-caption">迹时运动 AI教练的 3D 动作演示</figcaption>
  </figure>;
}
